import { Component, Input, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { ProspectSummary } from 'src/app/domain/prospect.model';
import { ProspectsService } from 'src/app/shared/services/prospects.service';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import { Chart } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-prospect-summary-sectors',
  templateUrl: './prospect-summary-sectors.component.html',
  styleUrls: ['./prospect-summary-sectors.component.scss']
})
export class ProspectSummarySectorsComponent implements OnInit, AfterViewInit {
  @ViewChild('dtProspectSummarySector') table!: Table;
  @ViewChild('prospectSummarySectorChart') prospectSummaryChart!: UIChart;

  @Input() userId: number = 0;

  // Data
  prospectSummary: ProspectSummary[] = [];
  filteredProspectSummary: ProspectSummary[] = [];

  // Chart
  prospectSummaryChartData: any = { labels: [], datasets: [] };
  prospectSummaryChartOptions: any;
  hiddenDatasets: boolean[] = [];

  // Filters & Controls
  sectorNameOptions: any[] = [];
  statusOptions: any[] = [];
  selectedProspectSummaryDataType: string = 'prospectCount';
  selectedProspectSummaryGroupBy: string = 'sectorName'; // default to sector
  selectedSectorScope: string = 'all';
  selectedSectorCount: number = 10;
  isSorted: boolean = false;
  currentYear = new Date().getFullYear();
  yearOptions: { label: string; value: number }[] = [];
  selectedYears: number[] = [];

  // Dropdown options
  prospectSummaryDataTypes = [
    { label: 'Prospect Count', value: 'prospectCount' },
    { label: 'Total Service Value', value: 'totalServiceValue' },
    { label: 'Total Activities', value: 'totalActivities' }
  ];

  prospectSummaryGroupByOptions = [
    { label: 'Sector Name', value: 'sectorName' },
    { label: 'Status', value: 'status' }
  ];

  sectorScopeOptions = [
    { label: 'Top', value: 'top' },
    { label: 'All', value: 'all' },
    { label: 'Bottom', value: 'bottom' }
  ];

  constructor(private prospectsService: ProspectsService) {
    Chart.register(this.customLabelsPlugin);
  }

  ngOnInit() {
    this.generateYearOptions();
    this.selectedYears = this.yearOptions.map(opt => opt.value);
    this.initializeChart();
    this.loadData();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.prospectSummaryChart?.chart) {
        this.prospectSummaryChart.chart.update();
      }
    }, 100);
  }

  private generateYearOptions() {
    this.yearOptions = [];
    for (let y = 2022; y <= this.currentYear; y++) {
      this.yearOptions.push({ label: y.toString(), value: y });
    }
  }

  loadData() {
    this.prospectsService.getProspectSummaryBySectors()
      .toPromise()
      .then(response => {
        this.prospectSummary = response;
        this.filteredProspectSummary = response;
        this.buildFilterOptions();
        this.updateChart();
      })
      .catch(error => console.error('Error loading sector summary:', error));
  }

  loadByYears(years: number[]) {
    this.prospectsService.getProspectSummaryBySectors(years)
      .toPromise()
      .then(response => {
        this.prospectSummary = response;
        this.filteredProspectSummary = response;
        this.updateChart();
      })
      .catch(error => {
        console.error('Error loading by years:', error);
        this.filteredProspectSummary = [];
        this.updateChart();
      });
  }

  private buildFilterOptions() {
    const uniqueSectors = [...new Set(this.prospectSummary.map(item => item.sectorName))];
    this.sectorNameOptions = uniqueSectors.map(name => ({ label: name, value: name }));

    const uniqueStatuses = [...new Set(this.prospectSummary.map(item => item.status))];
    this.statusOptions = uniqueStatuses.map(status => ({ label: status, value: status }));
  }

  initializeChart() {
    this.prospectSummaryChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      scales: {
        x: {
          stacked: true,
          ticks: {
            autoSkip: false,
            maxRotation: 90,
            minRotation: 90,
            padding: 4,
            font: { size: 10 }
          }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grace: '5%',
          ticks: { precision: 0 }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              const datasetLabel = tooltipItem.dataset.label;
              const value = tooltipItem.raw;
              if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
                return `${datasetLabel}: $${value.toLocaleString()}`;
              }
              return `${datasetLabel}: ${value}`;
            },
            footer: (tooltipItems) => {
              const dataIndex = tooltipItems[0].dataIndex;
              const allDatasets = tooltipItems[0].chart.data.datasets;
              let total = 0;
              for (let i = 0; i < allDatasets.length; i++) {
                total += allDatasets[i].data[dataIndex] || 0;
              }
              if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
                return `Total: $${total.toLocaleString()}`;
              }
              return `Total: ${total}`;
            }
          },
          usePointStyle: true,
          boxWidth: 6
        }
      }
    };
  }

  updateChart() {
    let dataToDisplay = [...this.filteredProspectSummary];

    if (this.selectedSectorScope === 'top' || (this.isSorted && this.selectedSectorScope === 'all')) {
      dataToDisplay.sort((a, b) => b[this.selectedProspectSummaryDataType] - a[this.selectedProspectSummaryDataType]);
    } else if (this.selectedSectorScope === 'bottom') {
      dataToDisplay.sort((a, b) => a[this.selectedProspectSummaryDataType] - b[this.selectedProspectSummaryDataType]);
    }

    if (this.selectedSectorScope === 'top' || this.selectedSectorScope === 'bottom') {
      dataToDisplay = dataToDisplay.slice(0, this.selectedSectorCount);
    }

    const groupedData = this.selectedProspectSummaryGroupBy === 'sectorName'
      ? this.groupDataBySector(dataToDisplay)
      : this.groupDataByStatus(dataToDisplay);

    const labels = Object.keys(groupedData);
    const datasets = this.createDatasets(groupedData);

    this.prospectSummaryChartData = { labels, datasets };
    this.hiddenDatasets = new Array(datasets.length).fill(false);

    if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
      this.prospectSummaryChartOptions.scales.y.ticks.callback = (value: number) =>
        value >= 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${value}`;
    } else {
      this.prospectSummaryChartOptions.scales.y.ticks = { precision: 0 };
    }

    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.update();
    }
  }

  groupDataBySector(data: ProspectSummary[]) {
    return data.reduce((acc, item) => {
      if (!acc[item.sectorName]) acc[item.sectorName] = {};
      acc[item.sectorName][item.status] = item[this.selectedProspectSummaryDataType];
      return acc;
    }, {} as Record<string, Record<string, number>>);
  }

  groupDataByStatus(data: ProspectSummary[]) {
    return data.reduce((acc, item) => {
      if (!acc[item.status]) acc[item.status] = {};
      acc[item.status][item.sectorName] = item[this.selectedProspectSummaryDataType];
      return acc;
    }, {} as Record<string, Record<string, number>>);
  }

  createDatasets(groupedData: Record<string, Record<string, number>>) {
    const colorPalette = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
      '#808080', '#008000', '#8B4513', '#00CED1', '#FFA500', '#FF00FF'
    ];
    const groups = new Set<string>();
    Object.values(groupedData).forEach(data => {
      Object.keys(data).forEach(group => groups.add(group));
    });
    const groupArray = Array.from(groups);
    return groupArray.map((group, index) => {
      const data = Object.keys(groupedData).map(key => groupedData[key][group] || 0);
      const color = colorPalette[index % colorPalette.length];
      return {
        label: group,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
  }

  // Event Handlers
  onTableFilter(event: any) {
    this.filteredProspectSummary = event.filteredValue || [];
    this.updateChart();
  }

  onYearSelectionChange() {
    this.loadByYears(this.selectedYears);
  }

  onProspectSummaryDataTypeChange() { this.updateChart(); }
  onProspectSummaryGroupByChange() { this.updateChart(); }
  onSectorScopeChange() { this.updateChart(); }
  onSortToggle() { this.isSorted = !this.isSorted; this.updateChart(); }

  onSectorCountChange() {
    if (this.selectedSectorCount < 1) this.selectedSectorCount = 1;
    const max = this.prospectSummary.length;
    if (this.selectedSectorCount > max) this.selectedSectorCount = max;
    this.updateChart();
  }

  // Export Methods
  exportProspectSummaryExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredProspectSummary.map(summary => ({
        'Sector Name': summary.sectorName,
        'Status': summary.status,
        'Prospect Count': summary.prospectCount,
        'Total Activities': summary.totalActivities,
        'Total Service Value': summary.totalServiceValue
      })));
      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(data, `ProspectSummary_Sector_${new Date().getTime()}.xlsx`);
    });
  }

  exportProspectSummaryPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');
    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Sector Name', dataKey: 'sectorName' },
      { header: 'Status', dataKey: 'status' },
      { header: 'Prospect Count', dataKey: 'prospectCount' },
      { header: 'Total Activities', dataKey: 'totalActivities' },
      { header: 'Total Service Value', dataKey: 'totalServiceValue' }
    ];
    const rows = this.filteredProspectSummary.map((row, index) => ({
      index: index + 1,
      sectorName: row.sectorName,
      status: row.status,
      prospectCount: row.prospectCount,
      totalActivities: row.totalActivities,
      totalServiceValue: row.totalServiceValue
    }));
    autoTable(doc, {
      columns,
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 20 }
    });
    doc.save('ProspectSummary_Sector.pdf');
  }

  exportProspectSummaryChartAsImage() {
    this.prepareChartForExport();
    const canvas = this.prospectSummaryChart.chart.canvas;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/png');
    ctx.restore();
    const link = document.createElement('a');
    link.href = base64Image;
    link.download = 'prospect-summary-sector-chart.png';
    link.click();
    setTimeout(() => this.resetChartAfterExport(), 100);
  }

  exportProspectSummaryChartAsPDF() {
    this.prepareChartForExport();
    const canvas = this.prospectSummaryChart.chart.canvas;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/png');
    ctx.restore();
    const imgWidth = 280;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;
    const pdf = new jsPDF('landscape');
    pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
    pdf.save('prospect-summary-sector-chart.pdf');
    setTimeout(() => this.resetChartAfterExport(), 100);
  }

  private prepareChartForExport() {
    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.options.plugins.legend.display = true;
      this.prospectSummaryChart.chart.options.plugins.legend.position = 'right';
      this.hiddenDatasets.forEach((hidden, i) => {
        const meta = this.prospectSummaryChart.chart.getDatasetMeta(i);
        meta.hidden = hidden;
      });
      this.prospectSummaryChart.chart.update();
    }
  }

  private resetChartAfterExport() {
    if (this.prospectSummaryChart?.chart) {
      this.prospectSummaryChart.chart.options.plugins.legend.display = false;
      this.prospectSummaryChart.chart.update();
    }
  }

  toggleDataset(index: number) {
    if (!this.prospectSummaryChart?.chart) return;
    this.hiddenDatasets[index] = !this.hiddenDatasets[index];
    const meta = this.prospectSummaryChart.chart.getDatasetMeta(index);
    meta.hidden = this.hiddenDatasets[index];
    this.prospectSummaryChart.chart.update();
  }

  isDatasetHidden(index: number): boolean {
    return this.hiddenDatasets[index] || false;
  }

  customLabelsPlugin = {
    id: 'customLabels',
    afterDraw: (chart: any) => {
      if (!chart?.ctx || !chart.scales?.y) return;
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      ctx.save();
      ctx.font = 'Bold 12px Helvetica';
      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const datasets = chart.data.datasets;
      if (!datasets?.length) { ctx.restore(); return; }
      chart.data.labels.forEach((_: any, index: number) => {
        let total = 0;
        datasets.forEach((dataset: any) => total += dataset.data[index] || 0);
        if (total > 0) {
          const lastMeta = chart.getDatasetMeta(datasets.length - 1);
          const bar = lastMeta.data[index];
          if (bar?.x !== undefined && yAxis.getPixelForValue) {
            const x = bar.x;
            const y = yAxis.getPixelForValue(total);
            let displayText = total.toString();
            if (this.selectedProspectSummaryDataType === 'totalServiceValue') {
              displayText = `$${total.toLocaleString()}`;
            }
            ctx.fillText(displayText, x, y - 5);
          }
        }
      });
      ctx.restore();
    }
  };
}